
// import { sha256 } from 'js-sha256';
// import moment from 'moment';

// import { apiKEY } from '../base/config/constants';
// import { getBackendSafeRoomName } from '../base/util';

import { getLocalParticipant } from '../base/participants';

import {
    ADD_MESSAGE,
    CLEAR_MESSAGES,
    CLOSE_CHAT,
    CLOSE_IMAGE_PREVIEW,
    DELETE_MESSAGE,
    DISMISS_FLOATING_CHAT_NOTIFICATION,
    EDIT_MESSAGE,
    OPEN_IMAGE_PREVIEW,
    RESTORE_MESSAGE,
    SEND_EDIT_MESSAGE,
    SEND_MESSAGE,
    SET_CHAT_NOTIFICATIONS_HIDDEN,
    SET_EDITING_MESSAGE,
    SET_IS_POLL_TAB_FOCUSED,
    SET_LOBBY_CHAT_ACTIVE_STATE,
    SET_LOBBY_CHAT_RECIPIENT,
    SET_PRIVATE_CHAT_DISABLED,
    SET_PRIVATE_MESSAGE_RECIPIENT,
    SET_VISITOR_TAB_FOCUSED,
    SHOW_FLOATING_CHAT_NOTIFICATION
} from './actionTypes';
import { LOBBY_CHAT_INITIALIZED } from './constants';

/**
 * Adds a chat message to the collection of messages.
 *
 * @param {Object} messageDetails - The chat message to save.
 * @param {any} messageDetails.displayName - The displayName of the
 * participant that authored the message.
 * @param {any} messageDetails.hasRead - Whether or not to immediately mark
 * the message as read.
 * @param {any} messageDetails.message - The received message to display.
 * @param {any} messageDetails.messageType - The kind of message, such as
 * "error" or "local" or "remote".
 * @param {any} messageDetails.timestamp - A timestamp to display for when
 * the message was received.
 * @param {any} messageDetails.isReaction - Whether or not the
 * message is a reaction message.
 * @returns {{
 *     type: ADD_MESSAGE,
 *     displayName: any,
 *     hasRead: any,
 *     message: any,
 *     messageType: any,
 *     timestamp: any,
 *     isReaction: any
 * }}
 */
export function addMessage(messageDetails: Object) {
    return {
        type: ADD_MESSAGE,
        ...messageDetails
    };
}

/**
 * Clears the chat messages in Redux.
 *
 * @returns {{
 *     type: CLEAR_MESSAGES
 * }}
 */
export function clearMessages() {
    return {
        type: CLEAR_MESSAGES
    };
}

/**
 * Edits an existing chat message.
 *
 * @param {Object} message - The chat message to edit/override. The messages will be matched from the state
 * comparing the messageId.
 * @returns {{
 *     type: EDIT_MESSAGE,
 *     message: Object
 * }}
 */
export function editMessage(message: Object) {
    return {
        type: EDIT_MESSAGE,
        message
    };
}

/**
 * Action to signal the closing of the chat dialog.
 *
 * @returns {{
 *     type: CLOSE_CHAT
 * }}
 */
export function closeChat() {
    return {
        type: CLOSE_CHAT
    };
}

/**
 * Sends a chat message to everyone in the conference.
 *
 * @param {string} message - The chat message to send out.
 * @param {boolean} ignorePrivacy - True if the privacy notification should be ignored.
 * @param {string} channel - Optional target chat thread. Pass 'visitor' to direct the message
 * to the visitor chat thread (used when a main-room participant posts into visitor chat).
 * @returns {{
 *     type: SEND_MESSAGE,
 *     ignorePrivacy: boolean,
 *     message: string,
 *     channel: string
 * }}
 */
export function sendMessage(message: string, ignorePrivacy: boolean = false, channel?: string) {
    return {
        type: SEND_MESSAGE,
        ignorePrivacy,
        message,
        channel
    };
}

/**
 * Initiates the sending of a private message to the supplied participant.
 *
 * @param {Participant} participant - The participant to set the recipient to.
 * @returns {{
 *     participant: Participant,
 *     type: SET_PRIVATE_MESSAGE_RECIPIENT
 * }}
 */
export function setPrivateMessageRecipient(participant: Object) {
    return {
        participant,
        type: SET_PRIVATE_MESSAGE_RECIPIENT
    };
}

/**
 * Set the value of _isPollsTabFocused.
 *
 * @param {boolean} isPollsTabFocused - The new value for _isPollsTabFocused.
 * @returns {Function}
 */
export function setIsPollsTabFocused(isPollsTabFocused: boolean) {
    return {
        isPollsTabFocused,
        type: SET_IS_POLL_TAB_FOCUSED
    };
}

/**
 * Sets the lobby room's chat active state.
 *
 * @param {boolean} value - The active state.
 *
 * @returns {Object}
 */
export function setLobbyChatActiveState(value) {
    return {
        type: SET_LOBBY_CHAT_ACTIVE_STATE,
        payload: value
    };
}

/**
 * Handles initial setup of lobby message between
 * Moderator and participant.
 *
 * @param {string} participantId - The participant id.
 *
 * @returns {Object}
 */
export function handleLobbyChatInitialized(participantId) {
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    return async (dispatch, getState) => {
        if (!participantId) {
            return;
        }
        const state = getState();
        const conference = state['features/base/conference'].conference;
        const { knockingParticipants } = state['features/lobby'];
        const { lobbyMessageRecipient } = state['features/chat'];
        const me = getLocalParticipant(state);
        const lobbyLocalId = conference?.myLobbyUserId();

        if (lobbyMessageRecipient && lobbyMessageRecipient.id === participantId) {
            return dispatch(setLobbyChatActiveState(true));
        }

        const attendee = knockingParticipants.find(p => p.id === participantId);

        if (attendee && attendee.chattingWithModerator === lobbyLocalId) {
            return dispatch({
                type: SET_LOBBY_CHAT_RECIPIENT,
                participant: attendee,
                open: true
            });
        }

        if (!attendee) {
            return;
        }

        const payload = {
            type: LOBBY_CHAT_INITIALIZED,
            moderator: {
                ...me,
                name: 'Moderator',
                id: lobbyLocalId
            },
            attendee
        };

        // notify attendee privately.
        conference?.sendLobbyMessage(payload, attendee.id);

        // notify other moderators.
        return conference?.sendLobbyMessage(payload);
    };
}


/**
 * Sets the message being edited in the chat input (React Native).
 *
 * @param {Object|null} message - The message object to edit, or null to clear.
 * @returns {{
 *     type: SET_EDITING_MESSAGE,
 *     editingMessage: Object|null
 * }}
 */
export function setEditingMessage(message: Object | null) {
    return {
        type: SET_EDITING_MESSAGE,
        editingMessage: message
    };
}

/**
 * Sends an edit for an existing chat message (XEP-0308, works with Prosody).
 * Works for both group and private messages.
 *
 * @param {string} messageId - The stanza id of the message to replace.
 * @param {string} message - The new message text.
 * @param {string} recipientId - Optional. For private messages, the participant id of the recipient.
 * @returns {{
 *     type: SEND_EDIT_MESSAGE,
 *     messageId: string,
 *     message: string,
 *     recipientId?: string
 * }}
 */
export function sendEditMessage(messageId, message, recipientId) {
    return {
        type: SEND_EDIT_MESSAGE,
        messageId,
        message,
        ...recipientId && { recipientId }
    };
}

/**
 * Deletes a chat message.
 *
 * @param {string} messageId - The id of the message to delete.
 * @param {number} [deletedAt] - The timestamp when the message was deleted.
 * @returns {{
 *     type: DELETE_MESSAGE,
 *     messageId: string,
 *     deletedAt: number
 * }}
 */
export function deleteChatMessage(messageId, deletedAt = Date.now()) {
    return {
        type: DELETE_MESSAGE,
        messageId,
        deletedAt
    };
}

/**
 * Restores a chat message.
 *
 * @param {string} messageId - The id of the message to restore.
 * @returns {{
 *     type: RESTORE_MESSAGE,
 *     messageId: string
 * }}
 */
export function restoreChatMessage(messageId) {
    return {
        type: RESTORE_MESSAGE,
        messageId
    };
}


/**
 * Opens the image preview modal with the specified image URL.
 *
 * @param {string} imageUrl - The URL of the image to preview.
 * @param {string} fileName - Optional file name for display.
 * @returns {{
 *     type: OPEN_IMAGE_PREVIEW,
 *     imageUrl: string,
 *     fileName: string
 * }}
 */
export function openImagePreview(imageUrl: string, fileName?: string) {
    return {
        type: OPEN_IMAGE_PREVIEW,
        imageUrl,
        fileName
    };
}

/**
 * Closes the image preview modal.
 *
 * @returns {{
 *     type: CLOSE_IMAGE_PREVIEW
 * }}
 */
export function closeImagePreview() {
    return {
        type: CLOSE_IMAGE_PREVIEW
    };
}

/**
 * Sets whether the visitor content panel (visitor chat / visitor polls) is
 * the active view for non-visitor main-room participants.
 *
 * @param {boolean} focused - True to show visitor content, false for main-room
 * content.
 * @returns {{
 *     type: SET_VISITOR_TAB_FOCUSED,
 *     focused: boolean
 * }}
 */
export function setVisitorTabFocused(focused: boolean) {
    return {
        type: SET_VISITOR_TAB_FOCUSED,
        focused
    };
}

/**
 * Sets whether private chat is disabled.
 *
 * @param {boolean} disabled - True if private chat is to be disabled, false otherwise.
 * @returns {{
 *     type: SET_PRIVATE_CHAT_DISABLED,
 *     disabled: boolean
 * }}
 */
export function setPrivateChatDisabled(disabled: boolean) {
    return {
        type: SET_PRIVATE_CHAT_DISABLED,
        disabled
    };
}

/**
 * Sets whether private chat is disabled and broadcasts the new state to all other participants.
 *
 * @param {boolean} disabled - True if private chat is to be disabled, false otherwise.
 * @returns {Function}
 */
export function setPrivateChatDisabledAndBroadcast(disabled: boolean) {
    return (dispatch: Function, getState: Function) => {
        dispatch(setPrivateChatDisabled(disabled));

        const state = getState();
        const { conference } = state['features/base/conference'];

        if (conference) {
            conference.sendMessage({
                type: 'SET_PRIVATE_CHAT_DISABLED',
                disabled,
                senderId: conference.myUserId()
            });
        }
    };
}

/**
 * Action to display the floating chat notification.
 *
 * @param {Object} notificationDetails - The details of the notification.
 * @returns {{
 *     type: SHOW_FLOATING_CHAT_NOTIFICATION,
 *     payload: Object
 * }}
 */
export function showFloatingChatNotification(notificationDetails: Object) {
    return {
        type: SHOW_FLOATING_CHAT_NOTIFICATION,
        payload: notificationDetails
    };
}

/**
 * Action to dismiss the floating chat notification.
 *
 * @returns {{
 *     type: DISMISS_FLOATING_CHAT_NOTIFICATION
 * }}
 */
export function dismissFloatingChatNotification() {
    return {
        type: DISMISS_FLOATING_CHAT_NOTIFICATION
    };
}

/**
 * Action to set whether floating chat notifications are hidden.
 *
 * @param {boolean} hidden - Whether chat notifications should be hidden.
 * @returns {{
 *     type: SET_CHAT_NOTIFICATIONS_HIDDEN,
 *     hidden: boolean
 * }}
 */
export function setChatNotificationsHidden(hidden: boolean) {
    return {
        type: SET_CHAT_NOTIFICATIONS_HIDDEN,
        hidden
    };
}

